import { searchApi } from './clients/api';
import { getEnv } from '../helpers/env';
import { BannerEntity, NewsFeedEntity } from '../types';
import { cleanObject } from '@teko/utils';

const transformBanner = (banner: Banner) =>
  ({
    id: banner.id.toString(),
    image: banner.image,
    priority: banner.priority,
    campaignName: banner.campaign_name,
    zone: banner.zone,
    targetPath: banner.target_path,
    urlType: banner.url_type,
    thumbnail: banner.thumbnail,
    description: banner.description,
    startDate: banner.start_date,
    endDate: banner.end_date,
  } as BannerEntity);

const transformNews = (news: News) =>
  cleanObject({
    metaDescription: news.meta_description,
    isActive: news.is_active,
    metaTitle: news.meta_title,
    updateAt: news.updated_at,
    imageUrl: news.image_url,
    isMagazine: news.is_magazine,
    metaKeyword: news.meta_keyword,
    name: news.name,
    id: news.id,
    terminals: news.terminals,
    content: news.content,
    urlKey: news.url_key,
  });

const cookBanners = ({
  data,
}: {
  data: { documents?: Banner[] };
}): Array<BannerEntity> => {
  return (data.documents || []).map(transformBanner);
};

const cookNews = ({
  data,
}: {
  data: { documents?: News[] };
}): Array<NewsFeedEntity> => {
  return (data?.documents || []).map(transformNews);
};

searchApi.addInterceptor((request: RequestInit) => {
  (request.headers as Headers).set(
    'x-tk-access-token',
    getEnv('SEARCH_API_ACCESS_TOKEN') as string,
  );
});

interface GetBannerBody {
  filters: { key: string; value: string | boolean }[];
  page_size: number;
  sorts: { field: string; type: string }[];
}

class SearchService {
  async getBanner(body: GetBannerBody): Promise<BannerEntity[]> {
    const res = await searchApi.post('/v1/banner/search', body);
    return cookBanners(res.data);
  }
  async getBannerByZone(
    zone: string,
    terminal: string,
  ): Promise<BannerEntity[]> {
    const filter: GetBannerBody = {
      filters: [
        {
          key: 'terminals',
          value: terminal,
        },
        {
          key: 'is_active',
          value: true,
        },
        {
          key: 'zone',
          value: zone,
        },
      ],
      page_size: 15,
      sorts: [
        {
          field: 'priority',
          type: 'ASC',
        },
      ],
    };
    return await this.getBanner(filter);
  }
  async getNewsFeed(terminal: string, slug?: string, page = 1, pageSize = 15) {
    const filters = [
      { key: 'terminals', value: terminal },
      { key: 'is_active', value: 1 },
    ];
    if (slug) {
      filters.push({
        key: 'url_key',
        value: slug,
      });
    }
    const res = await searchApi.post('/v1/pb_news/search', {
      filters,
      page_size: pageSize,
      page: page,
    });
    return {
      news: cookNews(res?.data || []),
      totalItems: res?.data?.data?.page_response?.total_items || 0,
      totalPages: res?.data?.data?.page_response?.total_page || 0,
    };
  }
}

export const searchService = new SearchService();

interface Banner {
  id: number;
  image: string;
  priority?: number;
  campaign_name?: string;
  zone?: string;
  target_path?: string;
  url_type?: string;
  thumbnail?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
}

interface News {
  meta_description?: string;
  is_active?: number;
  meta_title?: string;
  updated_at?: string;
  image_url?: string;
  is_magazine?: number;
  meta_keyword?: string;
  name?: string;
  id?: number;
  terminals?: string | string[];
  content?: string;
  url_key?: string;
}
